/**
 * Strateji editörünün canlı viewport'u — CSS değişkenleri.
 *
 * Zoom/pan sırasında hiçbir React render'ı olmaz: editör Fabric'in
 * viewport'unu her karede kapsayıcısına yazar (`--va`, `--ve`, `--vf`;
 * ekran = dünya × va + ve/vf) ve DOM katmanları konumlarını, boylarını, hatta
 * görünürlüklerini bu değişkenlerden **CSS ile** hesaplar. Tarayıcı hepsini
 * Fabric'in çizdiği karede birlikte günceller; hiçbir katman geride kalmaz.
 *
 * Denenip bırakılanlar (tekrar denenmesin):
 * - Her karede React state'i: 4.5k satırlık editör saniyede 60 kez render.
 * - Kaydedilmiş viewport'tan canlıya tek bir `scale()`: kuleler ve yazılar
 *   zoom bitene kadar büyüyüp küçülüyordu.
 * - Katman başına canlı abonelik (`setState` her karede): React güncellemeyi
 *   kendi zamanlamasıyla işliyor, katmanlar bir iki kare geride kalıp hızlı
 *   zoomda yerlerine "ışınlanıyordu", FPS de düştü.
 */

const VA = 'var(--va, 1)'
const VE = 'var(--ve, 0)'
const VF = 'var(--vf, 0)'

/** Dünya noktasının canlı ekran konumu (CSS transform parçası). */
export function pinAt(wx: number, wy: number): string {
  return `translate3d(calc(${wx} * ${VA} * 1px + ${VE} * 1px), calc(${wy} * ${VA} * 1px + ${VF} * 1px), 0)`
}

/** Dünya uzunluğunun canlı ekran boyu (CSS length). */
export function liveLen(w: number): string {
  return `calc(${w} * ${VA} * 1px)`
}

/** Dünya x'inin canlı ekran konumu + piksel payı (CSS length). */
export function liveX(wx: number, px = 0): string {
  return `calc(${wx} * ${VA} * 1px + ${VE} * 1px + ${px}px)`
}

/** Dünya y'sinin canlı ekran konumu + piksel payı (CSS length). */
export function liveY(wy: number, px = 0): string {
  return `calc(${wy} * ${VA} * 1px + ${VF} * 1px + ${px}px)`
}

/**
 * Dünya koordinatında çizilmiş bir SVG grubunu ekrana taşıyan dönüşüm.
 * İçindeki çizgiler `vectorEffect="non-scaling-stroke"` ile ekran pikselinde
 * kalınlığını korur.
 */
export const WORLD_GROUP_STYLE = {
  transformOrigin: '0 0',
  transform: `translate(calc(${VE} * 1px), calc(${VF} * 1px)) scale(${VA})`,
} as const

/**
 * Zoom ölçeği (`va`) `minScale`'i geçince 1, altında 0 — CSS'te basamak
 * fonksiyonu. "Yeterince yakınsa göster" kararları böylece React'siz ve
 * gecikmesiz verilir. `opacity`'ye yazılır.
 */
export function visibleFrom(minScale: number): string {
  if (!Number.isFinite(minScale)) return '0'
  return `clamp(0, calc((${VA} - ${minScale}) * 1000000), 1)`
}
